import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import styles from './EmployeePage.module.css';

const HR_EMAILS = ['gurpreet@densitypharmachem.com', 'prakash@densitypharmachem.com'];

const EMPTY_FORM = {
  employeeCode: '',
  name: '',
  email: '',
  designation: '',
  department: '',
  dateOfJoining: '',
  dateOfBirth: '',
  branch: '',
  reportingManager: '',
  hodName: '',
  isActive: true,
};

const exportToExcel = (employees) => {
  const headers = ['Emp Code','Name','Email','Designation','Department','Date of Joining','Date of Birth','Branch','Reporting Manager','HOD','Status'];
  const rows = employees.map((e) => [
    e.employeeCode || '',
    e.name || '',
    e.email || '',
    e.designation || '',
    e.department || '',
    e.dateOfJoining || '',
    e.dateOfBirth || '',
    e.branch || '',
    e.reportingManager || '',
    e.hodName || '',
    e.isActive ? 'Active' : 'Inactive',
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = `employees_${new Date().toISOString().slice(0,10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const EmployeePage = () => {
  const { user }   = useAuth();
  const navigate   = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [error,     setError]     = useState('');

  const [modalType,         setModalType]         = useState(null);
  const [selectedEmployee,  setSelectedEmployee]  = useState(null);
  const [formData,          setFormData]          = useState(EMPTY_FORM);
  const [formError,         setFormError]         = useState('');
  const [saving,            setSaving]            = useState(false);

  useEffect(() => {
    if (user !== null && user !== undefined && !HR_EMAILS.includes(user?.email?.toLowerCase())) {
      navigate('/appraisal');
    }
  }, [user, navigate]);

  const fetchEmployees = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const res = await api.getAllEmployees(searchTerm);
      if (res.success) setEmployees(res.data);
      else setError(res.message);
    } catch { setError('Failed to load employees.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  useEffect(() => {
    const timer = setTimeout(() => fetchEmployees(search), 400);
    return () => clearTimeout(timer);
  }, [search, fetchEmployees]);

  const openAdd  = () => { setFormData(EMPTY_FORM); setFormError(''); setModalType('add'); };
  const openEdit = (emp) => {
    setSelectedEmployee(emp);
    setFormData({
      employeeCode:     emp.employeeCode     || '',
      name:             emp.name             || '',
      email:            emp.email            || '',
      designation:      emp.designation      || '',
      department:       emp.department       || '',
      dateOfJoining:    emp.dateOfJoining    || '',
      dateOfBirth:      emp.dateOfBirth      || '',
      branch:           emp.branch           || '',
      reportingManager: emp.reportingManager || '',
      hodName:          emp.hodName          || '',
      isActive:         emp.isActive,
    });
    setFormError('');
    setModalType('edit');
  };
  // Names HR can pick as Reporting Manager / HOD — drawn from existing employees so the
  // value always matches a real record instead of a free-typed name that can't be mapped back.
  const employeeNames = [...new Set(employees.map((e) => e.name).filter(Boolean))].sort();

  const openDelete = (emp) => { setSelectedEmployee(emp); setModalType('delete'); };
  const closeModal = () => { setModalType(null); setSelectedEmployee(null); setFormError(''); };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) { setFormError('Name and Email are required.'); return; }
    setSaving(true); setFormError('');
    try {
      const res = modalType === 'add'
        ? await api.createEmployee(formData)
        : await api.updateEmployee(selectedEmployee.id, formData);
      if (res.success) { closeModal(); fetchEmployees(search); }
      else setFormError(res.message);
    } catch { setFormError('Something went wrong. Please try again.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await api.deleteEmployee(selectedEmployee.id);
      if (res.success) { closeModal(); fetchEmployees(search); }
      else setFormError(res.message);
    } catch { setFormError('Failed to delete employee.'); }
    finally { setSaving(false); }
  };

  return (
    <div className={styles.container}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Employees</h1>
          <p className={styles.subtitle}>Manage all employee records</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          {/* Search */}
          <div className={styles.searchBar}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Excel Export */}
          <button
            className={styles.excelBtn}
            onClick={() => exportToExcel(employees)}
            disabled={employees.length === 0}
            title="Export to Excel/CSV"
          >
            📊 Export
          </button>
        </div>

        <div className={styles.toolbarRight}>
          <button className={styles.addBtn} onClick={openAdd}>
            + Add Employee
          </button>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <div className={styles.tableScroll}>
          {loading ? (
            <div className={styles.loading}>Loading employees...</div>
          ) : employees.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>👥</div>
              <p>No employees found</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Emp Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Designation</th>
                  <th>Department</th>
                  <th>Date of Joining</th>
                  <th>Date of Birth</th>
                  <th>Branch</th>
                  <th>Reporting Manager</th>
                  <th>HOD</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, idx) => (
                  <tr key={emp.id}>
                    <td style={{ color: 'var(--color-text-light)', fontSize: 12 }}>{idx + 1}</td>
                    <td>{emp.employeeCode || '—'}</td>
                    <td><strong>{emp.name}</strong></td>
                    <td>{emp.email || '—'}</td>
                    <td style={{ maxWidth: 160, whiteSpace: 'normal', fontSize: 12 }}>{emp.designation || '—'}</td>
                    <td>{emp.department || '—'}</td>
                    <td>{emp.dateOfJoining || '—'}</td>
                    <td>{emp.dateOfBirth || '—'}</td>
                    <td>{emp.branch || '—'}</td>
                    <td>{emp.reportingManager || '—'}</td>
                    <td>{emp.hodName || '—'}</td>
                    <td>
                      <span className={`${styles.badge} ${emp.isActive ? styles.badgeActive : styles.badgeInactive}`}>
                        {emp.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button className={styles.editBtn} onClick={() => openEdit(emp)}>Edit</button>
                        <button className={styles.deleteBtn} onClick={() => openDelete(emp)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {!loading && employees.length > 0 && (
          <div className={styles.tableFooter}>
            <span>Showing {employees.length} employee{employees.length !== 1 ? 's' : ''}</span>
            <span style={{ color: 'var(--color-text-light)', fontSize: 11 }}>Scroll horizontally to see all columns →</span>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {(modalType === 'add' || modalType === 'edit') && (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>
                {modalType === 'add' ? '+ Add New Employee' : '✏️ Edit Employee'}
              </span>
              <button className={styles.modalClose} onClick={closeModal}>✕</button>
            </div>
            <div className={styles.modalBody}>
              {formError && <div className={styles.modalError}>{formError}</div>}
              <div className={styles.formGrid}>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Employee Code</label>
                  <input className={styles.input} name="employeeCode"
                    value={formData.employeeCode} onChange={handleFormChange}
                    placeholder="e.g. 1001" />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Full Name *</label>
                  <input className={styles.input} name="name"
                    value={formData.name} onChange={handleFormChange}
                    placeholder="Full name" />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Email Address *</label>
                  <input className={styles.input} name="email" type="email"
                    value={formData.email} onChange={handleFormChange}
                    placeholder="email@densitypharmachem.com" />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Designation</label>
                  <input className={styles.input} name="designation"
                    value={formData.designation} onChange={handleFormChange}
                    placeholder="e.g. Manager - HR" />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Department</label>
                  <select className={styles.select} name="department"
                    value={formData.department} onChange={handleFormChange}>
                    <option value="">Select Department</option>
                    <option>SALES</option>
                    <option>HUMAN RESOURCES</option>
                    <option>FINANCE</option>
                    <option>IT</option>
                    <option>QA</option>
                    <option>OPERATIONS & SUPPLYCHAIN</option>
                    <option>SERVICES</option>
                    <option>MANAGERIAL</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Date of Joining</label>
                  <input className={styles.input} name="dateOfJoining" type="date"
                    value={formData.dateOfJoining} onChange={handleFormChange} />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Date of Birth</label>
                  <input className={styles.input} name="dateOfBirth" type="date"
                    value={formData.dateOfBirth} onChange={handleFormChange} />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Branch</label>
                  <select className={styles.select} name="branch"
                    value={formData.branch} onChange={handleFormChange}>
                    <option value="">Select Branch</option>
                    <option>MUMBAI</option>
                    <option>HYDERABAD</option>
                    <option>EUROPE</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Reporting Manager</label>
                  <select className={styles.select} name="reportingManager"
                    value={formData.reportingManager} onChange={handleFormChange}>
                    <option value="">Select Reporting Manager</option>
                    {employeeNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>HOD</label>
                  <select className={styles.select} name="hodName"
                    value={formData.hodName} onChange={handleFormChange}>
                    <option value="">Select HOD</option>
                    {employeeNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Status</label>
                  <select className={styles.select} name="isActive"
                    value={formData.isActive}
                    onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.value === 'true' }))}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={closeModal}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : modalType === 'add' ? 'Add Employee' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {modalType === 'delete' && (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className={styles.modal} style={{ maxWidth: '400px' }}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>Delete Employee</span>
              <button className={styles.modalClose} onClick={closeModal}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.deleteConfirm}>
                <div className={styles.deleteIcon}>⚠️</div>
                <div className={styles.deleteTitle}>Are you sure?</div>
                <div className={styles.deleteDesc}>
                  You are about to delete{' '}
                  <span className={styles.deleteName}>{selectedEmployee?.name}</span>.
                  This action cannot be undone.
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={closeModal}>Cancel</button>
              <button className={styles.deleteConfirmBtn} onClick={handleDelete} disabled={saving}>
                {saving ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeePage;